// ─── ⭐⭐ IL LISTINO A GRIGLIA: listini, tabelle, caselle ─────────────────────
//
// 🗣️ Disegnato da lui il 17/09/2026: *«se mi fai trattare questa tabella come
// se fosse un foglio Excel… ogni tabella è una tipologia… metto una cifra in
// una casella, poi la trascino… basta scrivere la data di inizio e la data di
// fine, e se non si mette la data di fine non finisce mai… e la possibilità di
// dare un titolo a un listino»*.
//
// 🚨⭐⭐ QUESTO MODULO ESISTE PER FAR SPARIRE UN ALTRO MODULO. In `listino.ts`
// vivono specificità, strati, dominanza, ambiguità e conflitti: tutto
// quell'apparato esiste per arbitrare RIGHE CHE SI SOVRAPPONGONO. Le caselle
// non si sovrappongono — una mezz'ora di un giorno di una tabella ha un prezzo
// e basta — e l'arbitro non serve più.
// 📌 *La cura migliore per una regola difficile non è scriverla meglio: è
//    togliere il caso che la rendeva necessaria.*
//
// ⛔ E LA CASELLA VUOTA NON È ZERO — scelta sua, confermata il 17/09: vuoto
// vuol dire «non lo sappiamo» e la scheda si apre senza importo; zero è un
// prezzo, si scrive apposta e si legge «gratis». Qui la differenza sta nella
// FORMA dei dati: una casella vuota non è una casella con dentro zero, è una
// casella **che non c'è**. Così nessun codice futuro può confonderle.
import { z } from 'zod';
import { ORDINE_SETTIMANA, dataSchema, giornoDellaData, giornoSchema, oraSchema } from './circolo.js';
import { SIGLE_SETTIMANA } from './calendario.js';
/**
 * Le tipologie di una tabella dei giocatori: **partita e torneo**, e basta.
 *
 * 🔄 **Voce 19, tappa 19b (24/09/2026): la tipologia `pacchetto` non c'è più**,
 *    tolta insieme ai «Pacchetti di lezioni» (🗣️ *«via tutto»*).
 * 🔄 **Voce 19c (24/09/2026): se ne vanno anche `lezione` e `corso`.** Quanto
 *    paga un allievo lo dice **solo** il «Listino prezzi lezioni» (⇒
 *    `tariffe-lezioni.ts`), una cifra per tipo e per quanti sono — decisione sua,
 *    davanti alla domanda su due listini che rispondono alla stessa domanda.
 * ⚠️ Non è più `TIPI_PRENOTAZIONE`: quella lista resta per le vecchie righe del
 *    listino (`price_list`), che hanno ancora lezioni e corsi scritti dentro.
 */
export const TIPI_TABELLA_GIOCATORI = ['partita', 'torneo'];
export const tipoTabellaGiocatoriSchema = z.enum(TIPI_TABELLA_GIOCATORI);
/**
 * 💶⭐⭐ **`campo` È LA TABELLA DEI MAESTRI, e non si crea a mano** — voce 19,
 *    tappa 19a (24/09/2026).
 *
 * 🗣️ *«Questa voce deve riportare il prezzo che noi circolo facciamo al maestro
 * per quanto riguarda il costo orario del campo»*. ⇒ Un listino dei maestri ha
 * **una tabella sola**, di tipo `campo`, e la sua cella è il prezzo di **un'ora**
 * di campo che comincia in quella mezz'ora. Nasce insieme al listino: per
 * questo non sta fra i tipi che si scelgono in «+ Tabella».
 * 🔒 Il database tiene separati i due mondi (⇒
 *    `migrations/1758480000000_il_listino_dei_maestri.cjs`).
 */
export const TIPI_TABELLA = [...TIPI_TABELLA_GIOCATORI, 'campo'];
export const tipoTabellaSchema = z.enum(TIPI_TABELLA);
/**
 * Per chi è un listino: chi prenota (i giocatori) o chi affitta il campo per
 * farci lezione (i maestri). Sono due listini diversi, con la stessa griglia.
 */
export const DESTINATARI = ['giocatori', 'maestri'];
export const destinatariSchema = z.enum(DESTINATARI);
/**
 * La mezz'ora, e non l'ora.
 *
 * ⚠️ È il passo con cui il circolo fa cominciare le partite, ed è anche il
 * confine che una griglia oraria taglierebbe a metà: il picco del listino vero
 * comincia alle **19:30**.
 */
export const PASSO_GRIGLIA = 30;
export const cellaGrigliaSchema = z.object({
    weekday: giornoSchema,
    time: oraSchema,
    /** 💶 In centesimi. Zero è lecito e vuol dire gratis; «niente» è l'assenza. */
    priceCents: z.number().int().min(0),
});
export const tabellaPrezziSchema = z.object({
    id: z.uuid(),
    listId: z.uuid(),
    bookingType: tipoTabellaSchema,
    durationMinutes: z.number().int(),
    perPlayer: z.boolean(),
    celle: z.array(cellaGrigliaSchema),
});
export const listinoSchema = z.object({
    id: z.uuid(),
    destinatari: destinatariSchema,
    title: z.string(),
    validFrom: dataSchema,
    /** ⭐ Nullo = non finisce mai. Parole sue. */
    validTo: dataSchema.nullable(),
});
export const listinoCompletoSchema = listinoSchema.extend({
    tabelle: z.array(tabellaPrezziSchema),
});
export const elencoListiniSchema = z.object({ items: z.array(listinoCompletoSchema) });
// ── Quello che si scrive ────────────────────────────────────────────────────
export const creaListinoSchema = z.object({
    /** ⭐ Un listino dei maestri nasce con la sua tabella del campo già dentro. */
    destinatari: destinatariSchema.default('giocatori'),
    title: z.string().trim().min(1, 'Un listino senza titolo, fra tre mesi, non lo riconosce nessuno'),
    validFrom: dataSchema,
    validTo: dataSchema.nullable().default(null),
    /**
     * ⭐ Da quale listino COPIARE tutto: tabelle e caselle.
     *
     * 🗣️ Sua, 17/09/2026: *«la possibilità di duplicare un listino, così è più
     * semplice cambiare solamente alcuni prezzi»*.
     *
     * ⚖️ Titolo e date NON si copiano, ed è voluto — *«logicamente duplicandolo
     * bisogna reinserire il nome e la data»*: due listini con lo stesso titolo e
     * lo stesso periodo sarebbero indistinguibili nelle linguette, e a
     * `listinoPerData` toccherebbe arbitrare fra due gemelli.
     */
    copiaDa: z.uuid().nullable().default(null),
});
export const modificaListinoSchema = z
    .object({
    title: z.string().trim().min(1).optional(),
    validFrom: dataSchema.optional(),
    validTo: dataSchema.nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Non c’è niente da cambiare' });
export const creaTabellaSchema = z.object({
    /* ⛔ `campo` non c'è: nasce da sé insieme al listino dei maestri. */
    bookingType: tipoTabellaGiocatoriSchema,
    durationMinutes: z.number().int().min(5).max(600),
    perPlayer: z.boolean().default(true),
    /** ⭐ Da quale tabella copiare le caselle: una tabella nuova raramente nasce vuota. */
    copiaDa: z.uuid().nullable().default(null),
});
/**
 * Le caselle si scrivono A GRUPPI, e non una per volta.
 *
 * ⚖️ Non è un'ottimizzazione: è il gesto che lui ha chiesto. «Trascino la cifra
 * in basso» tocca trenta caselle, e trenta chiamate separate sarebbero trenta
 * modi di riuscire a metà — con la griglia che resta mezza piena e nessuno che
 * sa quali sono passate.
 *
 * ⛔ `priceCents: null` **cancella** la casella: è il modo di dire «questa
 * mezz'ora non ha prezzo», che è diverso da scriverci zero.
 */
export const scriviCelleSchema = z.object({
    celle: z
        .array(z.object({
        weekday: giornoSchema,
        time: oraSchema,
        priceCents: z.number().int().min(0).nullable(),
    }))
        .min(1)
        .max(2000),
});
// ── La regola, che adesso è una ricerca ─────────────────────────────────────
/**
 * Quale listino vale quel giorno.
 *
 * ⭐ **Vince quello che comincia più tardi**, ed è il modo in cui la gente
 * pensa ai listini: quello nuovo *sostituisce* il vecchio dal giorno in cui
 * comincia. A parità di inizio vince quello che finisce prima — un listino a
 * tempo determinato è più preciso di uno «di sempre» nato lo stesso giorno.
 *
 * ⚠️ Non c'è nessuna «ambiguità» da dichiarare, e non perché sia stata tolta:
 * perché qui i contendenti sono POCHI e hanno un nome. Due listini che si
 * accavallano si vedono nell'elenco; due righe che si contendono un caso no.
 */
export function listinoPerData(listini, data) {
    const validi = listini.filter((l) => data >= l.validFrom && (l.validTo === null || data <= l.validTo));
    if (validi.length === 0)
        return null;
    return validi.reduce((a, b) => (vincePrima(b, a) ? b : a));
}
function vincePrima(a, b) {
    if (a.validFrom !== b.validFrom)
        return a.validFrom > b.validFrom;
    const fa = a.validTo ?? '9999-12-31';
    const fb = b.validTo ?? '9999-12-31';
    if (fa !== fb)
        return fa < fb;
    return a.id < b.id;
}
/**
 * La casella che CONTIENE quell'ora.
 *
 * 🚨 Non «la casella che comincia a quell'ora»: una prenotazione delle 19:45
 * appartiene alla mezz'ora delle 19:30. È la stessa lezione della voce 188 del
 * gestionale di oggi — cercare l'inizio esatto lasciava senza prezzo sei
 * prenotazioni su trentadue, mentre il prezzo c'era.
 *
 * ⚠️ E si guarda la casella, non l'ultima disponibile prima: una tabella
 * riempita solo la mattina non deve far costare le 20:00 come le 12:00.
 */
export function cellaPerOra(celle, weekday, ora) {
    const t = minuti(ora);
    for (const c of celle) {
        if (c.weekday !== weekday)
            continue;
        const da = minuti(c.time);
        if (t >= da && t < da + PASSO_GRIGLIA)
            return c;
    }
    return null;
}
const NIENTE = (perche) => ({
    priceCents: null,
    perPlayer: true,
    listino: null,
    tabella: null,
    perche,
});
/**
 * Il prezzo di un caso, dentro i listini.
 *
 * ⛔ `null` vuol dire **«non lo sappiamo»**, e non zero. La scheda si apre
 * senza importo e lo scrive la segreteria: è la scelta che lui ha confermato
 * il 17/09, ed è quella che tiene distinte «gratis» e «non deciso».
 *
 * ⭐⭐ **A CAVALLO DI DUE PREZZI SI PAGA QUELLO DI PARTENZA.** 🗣️ Regola sua,
 * 18/09/2026: *«se un listino è a cavallo tra due fasce, prende l'importo
 * della prima fascia»*. ⇒ Una partita di novanta minuti attraversa TRE
 * mezz'ore, e le due che vengono dopo non entrano nel conto: decide la
 * casella in cui si **comincia**, e `durationMinutes` serve solo a scegliere
 * la tabella, mai le caselle.
 *
 * ⚖️ **Perché non è un arrotondamento ma la regola giusta**: chi prenota
 * legge il prezzo quando sceglie l'ora, cioè prima di sapere cosa c'è dopo.
 * Un prezzo spalmato sulle mezz'ore attraversate darebbe alla stessa partita
 * due importi diversi a seconda di dove finisce — e nessuno dei due sarebbe
 * quello che la segreteria ha annunciato.
 * 📌 *Il prezzo appartiene al momento in cui si entra in campo, non a tutti i
 *    momenti in cui ci si resta.*
 */
export function prezzoDaiListini(listini, caso) {
    const giorno = giornoDellaData(caso.date);
    if (giorno === null)
        return NIENTE('Quella data non è una data.');
    const listino = listinoPerData(listini, caso.date);
    if (listino === null) {
        return NIENTE(`Nessun listino copre il ${caso.date}.`);
    }
    const tabella = listino.tabelle.find((t) => t.bookingType === caso.bookingType && t.durationMinutes === caso.durationMinutes);
    if (tabella === undefined) {
        return NIENTE(`«${listino.title}» non ha una tabella per ${caso.bookingType} di ${caso.durationMinutes} minuti.`);
    }
    const cella = cellaPerOra(tabella.celle, giorno, caso.time);
    if (cella === null) {
        return {
            priceCents: null,
            perPlayer: tabella.perPlayer,
            listino: listino.title,
            tabella: tabella.id,
            perche: `In «${listino.title}» quella mezz'ora è vuota: il prezzo non è stato deciso.`,
        };
    }
    return {
        priceCents: cella.priceCents,
        perPlayer: tabella.perPlayer,
        listino: listino.title,
        tabella: tabella.id,
        perche: cella.priceCents === 0
            ? `«${listino.title}» dice che quella mezz'ora è gratis.`
            : `Dalla casella delle ${cella.time} di «${listino.title}».`,
    };
}
export function costoDelCampo(listini, caso) {
    const vuoto = (perche, listino = null) => ({
        cents: null,
        listino,
        prezziAllOra: [],
        perche,
    });
    const giorno = giornoDellaData(caso.date);
    if (giorno === null)
        return vuoto('Quella data non è una data.');
    const listino = listinoPerData(listini, caso.date);
    if (listino === null)
        return vuoto(`Nessun listino dei maestri copre il ${caso.date}.`);
    const tabella = listino.tabelle.find((t) => t.bookingType === 'campo');
    if (tabella === undefined)
        return vuoto(`«${listino.title}» non ha la tabella del campo.`, listino);
    const inizio = minuti(caso.time);
    const fine = inizio + caso.durationMinutes;
    /* 🧮 Centesimi × minuti: si divide per 60 una volta sola, in fondo. */
    let centesimiPerMinuti = 0;
    const prezzi = new Set();
    for (let t = inizio; t < fine;) {
        const confine = Math.min((Math.floor(t / PASSO_GRIGLIA) + 1) * PASSO_GRIGLIA, fine);
        /* ⚠️ Oltre la mezzanotte è il giorno dopo, che è un'altra riga: si dice
           «senza tariffa» invece di leggere la casella sbagliata. */
        if (t >= 24 * 60)
            return vuoto('La lezione passa la mezzanotte.', listino);
        const cella = cellaPerOra(tabella.celle, giorno, ora(t));
        if (cella === null) {
            return vuoto(`In «${listino.title}» la mezz'ora delle ${ora(t)} è vuota.`, listino);
        }
        centesimiPerMinuti += cella.priceCents * (confine - t);
        prezzi.add(cella.priceCents);
        t = confine;
    }
    return {
        cents: Math.round(centesimiPerMinuti / 60),
        listino,
        prezziAllOra: [...prezzi].sort((a, b) => a - b),
        perche: `Dalle caselle di «${listino.title}», mezz'ora per mezz'ora.`,
    };
}
function ora(t) {
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}
function minuti(ora) {
    const [h, m] = ora.split(':');
    return Number(h) * 60 + Number(m);
}
/** Le ore di una griglia, dall'apertura alla chiusura: sono le sue righe. */
export function oreDellaGriglia(apre, chiude) {
    const ore = [];
    for (let t = minuti(apre); t < minuti(chiude); t += PASSO_GRIGLIA) {
        ore.push(ora(t));
    }
    return ore;
}
// ── 🎾 Il campo al maestro, come lo LEGGE il maestro (voce 19d) ──────────────
/**
 * Un tratto di giornata in cui il campo costa la stessa cifra all'ora.
 * `alle` può valere `24:00`: è la fine della mezz'ora delle 23:30.
 */
export const pezzoDelCampoSchema = z.object({
    dalle: z.string(),
    alle: z.string(),
    centsPerHour: z.number().int().min(0),
});
/** I giorni che hanno gli stessi tratti. `pezzi` vuoto: quei giorni non hanno nessuna casella. */
export const gruppoDelCampoSchema = z.object({
    giorni: z.array(giornoSchema),
    pezzi: z.array(pezzoDelCampoSchema),
});
export const campoAParoleSchema = z.object({
    title: z.string(),
    validFrom: dataSchema,
    validTo: dataSchema.nullable(),
    gruppi: z.array(gruppoDelCampoSchema),
});
/**
 * 🎾⭐ **La griglia del campo detta a parole: da 336 caselle a tre righe.**
 *
 * 🗣️ *«Mi piacerebbe dentro la pagina delle rendicontazioni dell'app Maestri
 * far vedere le tariffe decise»* ⇒ il maestro non legge una griglia giorno ×
 * mezz'ora sul telefono: legge «lun–ven · 8:00–18:00 · 8,00 €».
 *
 * ⚖️ Due passi, e nessuno inventa niente:
 * ① dentro un giorno si uniscono le mezz'ore **consecutive allo stesso
 *    prezzo**. Un buco (una mezz'ora vuota) chiude il tratto: vuoto non è zero,
 *    e un tratto che lo scavalcasse direbbe un prezzo per un'ora che non ce l'ha;
 * ② si mettono insieme i giorni con **gli stessi identici tratti**, nell'ordine
 *    in cui li conta il circolo (dal lunedì).
 *
 * ⛔ `null` se il listino non c'è o non ha la tabella del campo: la schermata
 *    lo dice a parole, non con degli zeri.
 */
export function campoAParole(listino) {
    if (listino === null)
        return null;
    const tabella = listino.tabelle.find((t) => t.bookingType === 'campo');
    if (tabella === undefined)
        return null;
    const gruppi = [];
    const chiavi = [];
    for (const giorno of ORDINE_SETTIMANA) {
        const celle = tabella.celle
            .filter((c) => c.weekday === giorno)
            .sort((a, b) => minuti(a.time) - minuti(b.time));
        const pezzi = [];
        for (const c of celle) {
            const ultimo = pezzi.at(-1);
            if (ultimo && ultimo.alle === c.time && ultimo.centsPerHour === c.priceCents) {
                ultimo.alle = ora(minuti(c.time) + PASSO_GRIGLIA);
            }
            else {
                pezzi.push({ dalle: c.time, alle: ora(minuti(c.time) + PASSO_GRIGLIA), centsPerHour: c.priceCents });
            }
        }
        const chiave = JSON.stringify(pezzi);
        const i = chiavi.indexOf(chiave);
        if (i === -1) {
            chiavi.push(chiave);
            gruppi.push({ giorni: [giorno], pezzi });
        }
        else {
            gruppi[i].giorni.push(giorno);
        }
    }
    return { title: listino.title, validFrom: listino.validFrom, validTo: listino.validTo, gruppi };
}
/**
 * «lun–ven», «sab–dom», «lun, mer», «tutti i giorni».
 *
 * ⭐ Il trattino solo quando i giorni sono **di fila** e almeno tre: «lun–mar»
 * si legge peggio di «lun, mar», e un trattino su giorni sparsi direbbe che ci
 * stanno dentro anche quelli in mezzo.
 * ⚠️ «Di fila» si conta nell'ordine del circolo (lun … dom), non nei numeri del
 *    database, dove la domenica è 0 e starebbe prima del lunedì.
 */
export function etichettaDeiGiorni(giorni) {
    const posti = giorni
        .map((g) => ORDINE_SETTIMANA.indexOf(g))
        .filter((i) => i >= 0)
        .sort((a, b) => a - b);
    if (posti.length === 7)
        return 'tutti i giorni';
    const pezzi = [];
    for (let i = 0; i < posti.length;) {
        let j = i;
        while (j + 1 < posti.length && posti[j + 1] === posti[j] + 1)
            j += 1;
        if (j - i >= 2) {
            pezzi.push(`${SIGLE_SETTIMANA[posti[i]]}–${SIGLE_SETTIMANA[posti[j]]}`);
        }
        else {
            for (let k = i; k <= j; k += 1)
                pezzi.push(SIGLE_SETTIMANA[posti[k]]);
        }
        i = j + 1;
    }
    return pezzi.join(', ');
}
//# sourceMappingURL=listini.js.map